/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/types/study-assessment.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { useQuery } from '@apollo/client/react';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import {
    getFieldChangeItems,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
} from 'views/pages/admin/audit-log/service';
import { convertRules } from '@utils/convertor';
import {
    auditLogsForStudy_OpraQueries_auditLogsForStudy_AuditRecord,
    studyAssessmentHistory_OpraQueries_studyAssessmentHistory_StudyAssessment,
} from '@queries/generated/audit.generated';
import {
    studyCrfs,
    studyCrfs_OpraQueries_crfs_StudyCrf,
    studyCrfsVariables,
} from '@queries/generated/study-crfs.generated';
import { GET_STUDY_CRFS } from '@queries/study-crfs';

const propArray = ['CrfId'];

interface IStudyAssessmentRecordChangeGrid {
    oldRecord: studyAssessmentHistory_OpraQueries_studyAssessmentHistory_StudyAssessment & { CfId: { $oid: string } };
    newRecord: studyAssessmentHistory_OpraQueries_studyAssessmentHistory_StudyAssessment & { CfId: { $oid: string } };
    oldItem: studyAssessmentHistory_OpraQueries_studyAssessmentHistory_StudyAssessment;
    newItem: studyAssessmentHistory_OpraQueries_studyAssessmentHistory_StudyAssessment;
    record: auditLogsForStudy_OpraQueries_auditLogsForStudy_AuditRecord;
}

const StudyAssessmentRecordChangeGrid = ({
    oldRecord,
    newRecord,
    oldItem,
    newItem,
    record,
}: IStudyAssessmentRecordChangeGrid) => {
    const { data: dataCrfs } = useQuery<studyCrfs, studyCrfsVariables>(GET_STUDY_CRFS, {
        variables: {
            studyId: record?.studyId ?? '',
            timestamp: newItem?.updated,
        },
        fetchPolicy: 'no-cache',
    });

    const newCrfFormId = (item: studyAssessmentHistory_OpraQueries_studyAssessmentHistory_StudyAssessment) => {
        const foundNewCrf = dataCrfs?.crfs?.find((crf: studyCrfs_OpraQueries_crfs_StudyCrf) => crf.id === item?.crfId);
        return foundNewCrf?.subjectCode;
    };

    const refactoredOldRecord = {
        ...oldRecord,
        ...removePropertiesFromObject(oldRecord, propArray),
        crfFormId: newCrfFormId(oldItem),
        AssessmentRules: convertRules(oldItem?.assessmentRules),
        CfId: oldRecord?.CfId?.$oid,
    };
    const refactoredNewRecord = {
        ...newRecord,
        ...removePropertiesFromObject(newRecord, propArray),
        crfFormId: newCrfFormId(newItem),
        AssessmentRules: convertRules(newItem?.assessmentRules),
        CfId: newRecord?.CfId?.$oid,
    };

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
        }),
    );

    return <FieldChangeGrid items={items} />;
};

export { StudyAssessmentRecordChangeGrid };
